import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, X, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

import { API } from "../lib/api";
// API now resolved at runtime via /lib/api.js
/**
 * PaymentPoller — Reads ?payment=success&session_id=... on mount, polls backend
 * until paid, then refreshes user (is_pro). Renders an in-page status toast.
 */
export const PaymentPoller = () => {
  const { token, refreshUser } = useAuth();
  const { t } = useLanguage();
  const p = t.pro;
  const [state, setState] = useState("idle"); // idle | polling | success | cancelled | error

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "cancel") {
      setState("cancelled");
      cleanUrl();
      return;
    }
    if (payment !== "success" || !sessionId) return;
    if (!token) return; // need auth to poll

    let attempts = 0;
    const MAX = 12;
    let cancelled = false;
    setState("polling");

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.clone().json();
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          cleanUrl();
          return;
        }
        if (data.status === "expired") {
          setState("error");
          cleanUrl();
          return;
        }
      } catch {
        // continue polling
      }
      if (attempts >= MAX) {
        setState("error");
        cleanUrl();
        return;
      }
      setTimeout(tick, 2000);
    };

    tick();
    return () => { cancelled = true; };
  }, [token, refreshUser]);

  if (state === "idle") return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-5 left-1/2 z-[110] -translate-x-1/2"
        data-testid={`payment-toast-${state}`}
      >
        <div
          className={`flex items-center gap-3 rounded-2xl border px-5 py-3 backdrop-blur-xl shadow-2xl ${
            state === "success"
              ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200 shadow-emerald-500/20"
              : state === "polling"
              ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-100 shadow-cyan-500/20"
              : state === "cancelled"
              ? "border-amber-400/40 bg-amber-400/10 text-amber-200"
              : "border-red-400/40 bg-red-400/10 text-red-200"
          }`}
        >
          {state === "polling" && <Loader2 className="h-4 w-4 animate-spin" />}
          {state === "success" && <CheckCircle2 className="h-5 w-5" />}
          {state === "cancelled" && <AlertCircle className="h-4 w-4" />}
          {state === "error" && <AlertCircle className="h-4 w-4" />}
          <span className="text-sm font-bold">
            {state === "polling" && p.processing}
            {state === "success" && p.success}
            {state === "cancelled" && p.cancelled}
            {state === "error" && (t.auth?.error_generic || "Error")}
          </span>
          <button
            onClick={() => setState("idle")}
            className="ml-2 opacity-60 hover:opacity-100"
            aria-label="dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

function cleanUrl() {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("payment");
    u.searchParams.delete("session_id");
    window.history.replaceState({}, "", u.toString());
  } catch {}
}

export default PaymentPoller;
